/**
 * The landing panel: a lightweight About at the top of the list (REQ-WEB-036).
 *
 * The owner's idea, from the visitor pass: the phone's first screen said
 * nothing about what the site is (the tagline is hidden at phone width), and
 * About lived below 25 cards in the footer. This says it in two sentences and
 * links on, then gets out of the way.
 *
 * Collapsing is remembered in localStorage, "as far as the browser allows":
 * private windows and blocked storage throw, and then the panel simply shows
 * again next time. Collapsed, it keeps a one-line handle so the explanation is
 * never unreachable, only out of the way.
 */
import { useState } from "preact/hooks";

const STORE_KEY = "lens-specs.landing";

function rememberedCollapsed(): boolean {
  try {
    return localStorage.getItem(STORE_KEY) === "collapsed";
  } catch {
    return false; // storage blocked: show it, which is the safe default
  }
}

function remember(collapsed: boolean): void {
  try {
    if (collapsed) localStorage.setItem(STORE_KEY, "collapsed");
    else localStorage.removeItem(STORE_KEY);
  } catch {
    // Not an error: the panel works without memory and shows again next visit.
  }
}

/**
 * Collapsed-state memory, held by the app so the panel and its handle agree.
 * The handle lives in the masthead row rather than on a row of its own: the
 * phone's top third is budgeted (REQ-WEB-019) and a handle on its own line cost
 * 36-44 px of it, pushing the first result past the limit.
 */
export function useLanding(): [boolean, (collapsed: boolean) => void] {
  const [collapsed, setCollapsed] = useState(rememberedCollapsed);
  return [collapsed, (next: boolean) => {
    setCollapsed(next);
    remember(next);
  }];
}

/**
 * Shown in the masthead only while the panel is collapsed. It shares that row
 * with the search box (REQ-WEB-037), so it is a round "?" whose accessible
 * name and tooltip carry the full question.
 */
export function LandingHandle(props: { onOpen: () => void }) {
  return (
    <button type="button" class="landing-handle compact" aria-expanded="false"
            aria-controls="landing-body" aria-label="What is lens-specs?"
            title="What is lens-specs?" onClick={props.onOpen}>
      ?
    </button>
  );
}

export function LandingPanel(props: { onHide: () => void }) {
  return (
    <section class="landing" id="landing-body" aria-label="About this site">
      <p class="landing-text">
        Camera lens specifications where <strong>every figure says where it came from</strong>.
        Tap any value to see its source, and when sources disagree you see both.
      </p>
      <p class="landing-links">
        <a href="/about">About</a>
        <a href="/methodology">How values are chosen</a>
        <button type="button" class="landing-hide" aria-expanded="true"
                aria-controls="landing-body" onClick={props.onHide}>
          Hide
        </button>
      </p>
    </section>
  );
}
